import { isTruthy } from '@ardoq/common-helpers';
import {
  buildMinimalScopeDataForFilters,
  ComponentTypeForLegend,
  componentTypeToLegendItem,
  getActiveConditionalFormattingForLegendPure,
  GraphComponentViewLegendProps,
} from '@ardoq/view-legend';
import {
  GraphInterface,
  ParentChildGraphEdge,
  RawGraphEdge2,
  RawGraphNode2,
} from '@ardoq/graph';
import type { I18nLocale } from '@ardoq/i18n';
import { uniqWith, isEqual } from 'lodash';
import {
  APIReferenceType,
  DiffMode,
  FormattingFilter,
  type APIOrganizationUser,
} from '@ardoq/api-types';
import { ScopedDiff } from '@ardoq/data-model';
import {
  hasDefaultGreyColorComponentFilter,
  hasDefaultGreyColorReferenceFilter,
  maybeGrayOutTypes,
} from 'models/utils/filterUtils';

type GetGraphComponentLegendPropsArgs = {
  componentMap: Map<string, RawGraphNode2>;
  groupMap: Map<string, RawGraphNode2>;
  edges: Array<RawGraphEdge2>;
  graphInterface: GraphInterface;
  formattingFilters: FormattingFilter[];
  scopeDiff: ScopedDiff | null;
  displayShapesInsteadOfIcons: boolean;
  activeDiffMode: DiffMode | null;
  i18nLocale: I18nLocale;
  users: APIOrganizationUser[];
  isNewConditionalFormatting: boolean;
};

const extractComponentTypesFromNodes = (
  nodes: Map<string, RawGraphNode2>,
  graphInterface: GraphInterface
): ComponentTypeForLegend[] => {
  const nodeComponentTypes = [...nodes].map(([_, { modelId }]) => {
    if (!modelId || !graphInterface.isComponent(modelId)) return null;

    const componentType = graphInterface.getComponentType(modelId);
    return componentType ? componentTypeToLegendItem(componentType) : null;
  });

  return uniqWith(nodeComponentTypes.filter(isTruthy), isEqual);
};

const getModelIdFromEdge = (edge: RawGraphEdge2): string =>
  edge.modelIds && edge.modelIds.length > 0
    ? edge.modelIds[0]
    : (edge.modelId ?? '');

const extractReferenceTypesFromEdges = (
  edges: RawGraphEdge2[],
  graphInterface: GraphInterface,
  i18nLocale: I18nLocale
): APIReferenceType[] => {
  const lateralEdges = edges.filter(edge => !edge.isParentChildReference);
  const hasParentChildEdges = edges.length > lateralEdges.length;

  const lateralReferenceTypes = uniqWith(
    lateralEdges
      .map(edge =>
        graphInterface.getReferenceModelType(getModelIdFromEdge(edge))
      )
      .filter(isTruthy),
    isEqual
  );

  return hasParentChildEdges
    ? [...lateralReferenceTypes, ParentChildGraphEdge.getType(i18nLocale)]
    : lateralReferenceTypes;
};

export const getGraphComponentLegendProps = ({
  componentMap,
  groupMap,
  edges,
  graphInterface,
  formattingFilters,
  scopeDiff,
  displayShapesInsteadOfIcons,
  activeDiffMode,
  i18nLocale,
  users,
  isNewConditionalFormatting,
}: GetGraphComponentLegendPropsArgs): GraphComponentViewLegendProps => {
  const scopeData = buildMinimalScopeDataForFilters(
    graphInterface,
    formattingFilters,
    users
  );

  const activeConditionalFormatting =
    getActiveConditionalFormattingForLegendPure(
      scopeData,
      formattingFilters,
      i18nLocale,
      isNewConditionalFormatting
    );

  const componentTypesFromComponents = extractComponentTypesFromNodes(
    componentMap,
    graphInterface
  );
  const componentTypesFromGroups = extractComponentTypesFromNodes(
    groupMap,
    graphInterface
  );
  const baseComponentTypes = uniqWith(
    [...componentTypesFromComponents, ...componentTypesFromGroups],
    isEqual
  );

  // Apply grey color to component types when "Keep original color" is unchecked
  // and shapes are displayed. Only apply when conditional formatting is already active
  // to avoid UI issues during conditional formatting creation.
  const hasActiveFormatting =
    activeConditionalFormatting.components.length > 0 ||
    activeConditionalFormatting.references.length > 0 ||
    activeConditionalFormatting.tags.length > 0;

  const componentTypes = maybeGrayOutTypes(
    baseComponentTypes,
    hasActiveFormatting &&
      displayShapesInsteadOfIcons &&
      hasDefaultGreyColorComponentFilter(formattingFilters)
  );

  const baseReferenceTypes = extractReferenceTypesFromEdges(
    edges,
    graphInterface,
    i18nLocale
  );

  // Apply grey color to reference types when "Keep original color" is unchecked
  // Only apply when conditional formatting is already active.
  const referenceTypes = maybeGrayOutTypes(
    baseReferenceTypes,
    hasActiveFormatting && hasDefaultGreyColorReferenceFilter(formattingFilters)
  );

  return {
    displayShapesInsteadOfIcons,
    componentTypes,
    referenceTypes,
    activeConditionalFormatting,
    activeDiffMode: scopeDiff ? activeDiffMode : null,
  };
};
